import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import type { SavedView } from "@laborchart-modules/common";
import type {
   IServerFilter,
   DataTableConfig,
   ColumnDefinition,
   CellValueChangeParams,
} from "@procore/data-table";
import { MultiSelectFilterRenderer } from "@procore/data-table";
import type { ChipFilter } from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";
import { getDetachedDay, getAttachedDate } from "@laborchart-modules/common/dist/datetime";
import {
   findCustomFieldByIntegrationName,
   findCustomFieldById,
} from "@/react/shared/custom-field-utils";
import { CustomFieldType } from "@laborchart-modules/common/dist/rethink/schemas/common/custom-field-type";

import {
   ReportType,
   type ColumnMap,
   type CommonStyleProps,
   type CustomField,
   type FormattedOption,
   type PersonName,
   type RawOption,
   GroupByColumn,
   EntityTypes,
} from "../prop-types";
import type { ValueValidatorParams } from "@procore/data-table";

import { requestContext } from "@/stores/common/request-context";
import type {
   IntegratedField,
   PersonOption,
   QRCodeDownloadURLProps,
   RoleWithNamesAndColor,
   SubProperties,
   ValueValidatorResponse,
} from "@/react/prop-types";
import MultiSelectPillFilter from "../components/data-table/custom-filters/multiselect-pill-filter";
import { StatusFilter } from "../components/data-table/custom-filters/status-filter";
import { DateFilter } from "../components/data-table/custom-filters/date-filter";
import { NumericValueFilter } from "../components/data-table/custom-filters/numeric-value-filter";
import { HidePastTimeOffFilter } from "../components/time-off-list/custom-filters/hide-past-time-off-filter";
import { TextFilter } from "../components/data-table/custom-filters/text-filter";
import { BooleanFilter } from "../components/data-table/custom-filters/bool-filter";
import type { ProjectDetailsFields } from "../components/tearsheets/project/types";
import type { AppliedRoleOption } from "@laborchart-modules/lc-core-api/dist/api/projects/get-applied-role-options";
import { generateProjectRoleColumnDefinitions } from "../components/project-list/helpers";
import {
   checkFilterIsRoles,
   type ListServerFilter,
} from "../components/data-table/data-table-utils";
import type { DateTimeOptions } from "@procore/globalization-toolkit";
import { DateTimeFormatter } from "@procore/globalization-toolkit";
import type { PersonDetailFields } from "../components/tearsheets/people/prop-types";
import type { PillColor, PillOption, useI18nContext } from "@procore/core-react";
import { PageType } from "../components/tearsheets/types";
import {
   legacyFieldNamesMap,
   subPropertiesColumnHeaderMap,
   subPropertiesColumnNameMap,
   subPropertiesConfig,
} from "../components/people-list/helpers";
import { ProjectRolesFilter } from "../components/data-table/custom-filters/project-roles-filter";
import { SavedViewPage } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import {
   customFieldFilterEntityTypeMap,
   customFieldSortEntityTypeMap,
   entityTypeMap,
   reverseEntityTypeMap,
} from "../components/assignment-list/assignment-list-helpers";
import { timeOptions } from "@/lib/utils/timezones";
import type { SerializedStatus } from "@laborchart-modules/common/dist/rethink/serializers";
import type { SerializedCachedEquipmentCompanyType } from "@laborchart-modules/common/dist/postgres/serializers/cached-equipment-company-type-serializer";

//function to truncate text
export const truncateText = (text: string, maxLength: number) => {
   if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
   }
   return text;
};

// This is the default name for the auto group column in AG Grid
const rowGroupColFieldName = "ag-Grid-AutoColumn";

/**
 * When saving a view: extracts sort_by and sort_direction from the AG Grid auto group column
 * and maps them to the actual row group column's field name in the saved view format.
 */
function extractGroupColumnSort(
   columnState: any[],
   columnHeadersMap: { [key: string]: string },
): { sort_by?: string; sort_direction?: Order } {
   const rowGroupColWithSort = columnState.find(
      (c: any) => c.field === rowGroupColFieldName && c.sort !== null,
   );
   const rowGroupCol = columnState.find((c: any) => c.rowGroup === true);

   if (rowGroupColWithSort && rowGroupCol) {
      return {
         sort_by: Object.keys(columnHeadersMap).find(
            (key) => columnHeadersMap[key] === rowGroupCol.field,
         ),
         sort_direction: rowGroupColWithSort.sort === "asc" ? Order.ASCENDING : Order.DESCENDING,
      };
   }
   return {};
}

/**
 * When restoring a view: if a row group column is sorted, prepends the AG Grid auto group column
 * with the same sort so the sort direction icon is visible on the grouped column header.
 */
function applyGroupColumnSort(columnState: any[], sortDirection?: Order | null): any[] {
   const hasGroupedSort = columnState.some((col: any) => col.rowGroup && col.sort !== null);
   if (!hasGroupedSort || !sortDirection) return columnState;

   return [
      {
         field: rowGroupColFieldName,
         hidden: false,
         width: 200,
         sort: sortDirection === Order.ASCENDING ? "asc" : "desc",
         sortIndex: 0,
         rowGroup: false,
         rowGroupIndex: null,
         pinned: null,
      },
      ...columnState,
   ];
}

// Function to sort an array by a given key in the specified order (default is ascending)
// can be extended to handle more complex sorting logic
export const sortArray = (arrayToSort: any[], key: string, order: Order = Order.ASCENDING) => {
   return [...arrayToSort].sort((a, b) => {
      const valueA = a[key];
      const valueB = b[key];

      if (order === Order.DESCENDING) {
         return valueB - valueA;
      } else {
         return valueA - valueB;
      }
   });
};

export function convertSavedViewToDataTableConfig(
   savedView: SavedView,
   columnHeadersMap: ColumnMap,
   defaultListTableConfig: DataTableConfig,
   filterFieldMap: ColumnMap,
   filterRendererMap: ColumnMap,
   customFields?: CustomField[],
   roleOptions?: AppliedRoleOption[],
) {
   const columnHeadersMapWithCustomFields = {
      ...columnHeadersMap,
      ...customFields?.reduce((acc: any, field: any) => {
         const fieldName = field.entity ? `${field.entity}'s ${field.name}` : field.name;
         const fieldIntegrationName = field.entity
            ? `${field.entity}'s ${field.integration_name}`
            : field.integration_name;
         acc[fieldName] = fieldIntegrationName;
         return acc;
      }, {}),
   };

   const dtSortBy = columnHeadersMapWithCustomFields[savedView.view_config.sort_by!];
   const columnGroupState: Array<{ groupId: string | number; open: boolean }> = [];

   // Create an array to hold new columns for "current_assignments"
   const columnState =
      savedView.view_config.column_headers
         ?.map((x: any, index: number) => {
            let dtColumnName = "";
            let sort: string | null = null;

            if (x.key === "custom_fields") {
               if (savedView.page === SavedViewPage.ASSIGNMENTS_LIST) {
                  {
                     const customField = findCustomFieldById(customFields ?? [], x.meta?.field_id);
                     if (customField) {
                        dtColumnName = `${reverseEntityTypeMap[x.meta?.field_entity]}'s ${
                           customField.integration_name
                        }`;
                        // Only apply sorting if both custom_field_id and entity match the sort_by configuration
                        if (
                           customField.id === savedView.view_config.custom_field_id &&
                           savedView.view_config.sort_by ===
                              customFieldSortEntityTypeMap[
                                 reverseEntityTypeMap[x.meta?.field_entity]
                              ]
                        ) {
                           sort = getSortDirection(savedView.view_config.sort_direction!);
                        }
                     }
                  }
               } else if (savedView.page === SavedViewPage.REQUESTS) {
                  const customField = findCustomFieldById(customFields ?? [], x.meta?.field_id);
                  if (customField) {
                     dtColumnName = customField.integration_name;
                  }
               } else {
                  dtColumnName = columnHeadersMapWithCustomFields[x.name];
               }
            } else if (x.key === "positions") {
               dtColumnName = `positions_${x.key_id}`;
            } else {
               dtColumnName = columnHeadersMapWithCustomFields[x.key];
            }

            if (!dtColumnName) {
               return;
            }

            if (dtColumnName == dtSortBy) {
               sort = getSortDirection(savedView.view_config.sort_direction!);
            }

            // This is a hack to make the column groupable if the sub_properties are present in the saved view - People List (job_title), Request List (project)
            let rowGroupCheck = false;

            if (dtColumnName === GroupByColumn.JobTitle || dtColumnName === GroupByColumn.Project) {
               rowGroupCheck = x.sub_properties[0]?.enabled ?? false;
            }

            const newColumns: any[] = [];
            // People List - If the column is "current_assignments"/"next_assignment", we need to add the sub properties as columns and nested under the main column
            if (
               dtColumnName === legacyFieldNamesMap.current_assignments ||
               dtColumnName === legacyFieldNamesMap.next_assignment
            ) {
               columnGroupState.push({
                  groupId: dtColumnName,
                  open: false,
               });

               if (dtColumnName + "_project_name" === dtSortBy)
                  sort = getSortDirection(savedView.view_config.sort_direction!);

               // Add a column for project name
               newColumns.push({
                  field: dtColumnName + "_project_name",
                  hidden: false,
                  width: 140,
                  rowGroup: false,
                  flex: null,
                  pinned: false,
                  sort: sort,
                  sortIndex: null,
               });

               const sortBy = subPropertiesColumnHeaderMap[savedView.view_config.sort_by!];

               // Add sub-property columns (Project Number, Start Date, End Date)
               x.sub_properties.forEach((subProperty: any) => {
                  if (
                     subProperty.key === subPropertiesColumnNameMap.assignment_job_number ||
                     subProperty.key === subPropertiesColumnNameMap.assignment_start_date ||
                     subProperty.key === subPropertiesColumnNameMap.assignment_end_date
                  ) {
                     let subPropertySort = null;
                     if (dtColumnName + "_" + subProperty.key === sortBy)
                        subPropertySort = getSortDirection(savedView.view_config.sort_direction!);

                     newColumns.push({
                        field: dtColumnName + "_" + subProperty.key,
                        hidden: false,
                        width: subProperty.width,
                        rowGroup: false,
                        flex: null,
                        pinned: false,
                        sort: subPropertySort,
                        sortIndex: null,
                     });
                  }
               });
            } else {
               // For other columns, add to columnGroupState
               columnGroupState.push({
                  groupId: index.toString(),
                  open: false,
               });

               // Add the main column
               newColumns.push({
                  field: dtColumnName,
                  hidden: rowGroupCheck,
                  width: x.width,
                  rowGroup: rowGroupCheck,
                  flex: x.sequence,
                  pinned: x.pinned ?? null,
                  sort: sort,
                  sortIndex: null,
               });
            }

            // Return the newColumns array, which contains all flattened columns
            return newColumns;
         })
         .flat() ?? [];

   // Remove any undefined values from the column state
   columnState.splice(0, columnState.length, ...columnState.filter((item) => item !== undefined));

   const finalColumnState = columnState;

   // If the column isn't present in the saved view, it should be added to the column state as hidden
   defaultListTableConfig.columnState.forEach((x: any) => {
      const isPresentInSavedView = finalColumnState.find((y: any) => x.field == y.field);
      if (!isPresentInSavedView) {
         finalColumnState.push({
            ...x,
            hidden: true,
         });
      }
   });

   // Generate the project role columns and if they are note present in the saved view,
   // Add them as hidden
   generateProjectRoleColumnDefinitions(roleOptions).forEach((cd: ColumnDefinition) => {
      const keyToMatch = cd.field.split("_")[0];
      const keyIdToMatch = cd.field.split("_")[1];
      const isPresentInSavedView = savedView.view_config.column_headers?.find((sv: any) => {
         if (sv.key === keyToMatch && sv.key_id === keyIdToMatch) {
            return sv;
         }
      });

      if (!isPresentInSavedView) {
         finalColumnState.push({
            field: cd.field,
            hidden: true,
            width: 140,
            sort: null,
            sortIndex: null,
            rowGroup: false,
            pinned: false,
            flex: null,
         });
      }
   });

   const finalColumnStateWithGroupSort = applyGroupColumnSort(
      finalColumnState,
      savedView.view_config.sort_direction,
   );

   const serverFilters: IServerFilter[] = [];
   const filtersData = savedView.chip_filters?.reduce(
      (result, item) => {
         if (item.property?.includes("custom_fields")) {
            result.customFiltersData.push(item);
         } else if (item.property === "project_roles") {
            result.rolesFilterData.push(item);
         } else {
            result.noncustomFiltersData.push(item);
         }
         return result;
      },
      { customFiltersData: [] as any, noncustomFiltersData: [] as any, rolesFilterData: [] as any },
   );

   if (filtersData?.noncustomFiltersData.length > 0) {
      filtersData?.noncustomFiltersData.forEach((filter: ChipFilter) => {
         const dtFilter = convertToIServerFilter(filter, filterFieldMap, filterRendererMap);
         if (dtFilter) {
            const maybeExisitngFilterIndex = serverFilters.findIndex(
               (x) => x.field === dtFilter.field,
            );

            // If a filter has already been added for the field, only update the value and selected attributes
            if (maybeExisitngFilterIndex !== -1) {
               const existingFilter = serverFilters[maybeExisitngFilterIndex];
               const updatedFilter = {
                  ...existingFilter,
                  value: [...existingFilter.value, ...dtFilter.value],
                  selected: [...existingFilter.selected, ...dtFilter.selected],
               };

               // Replace the existing filter with the updated one
               serverFilters[maybeExisitngFilterIndex] = updatedFilter;
            }
            // Otherwise, add the entire filter
            else {
               serverFilters.push(dtFilter);
            }
         }
      });
   }
   if (filtersData?.customFiltersData.length > 0) {
      const dtFilter = transformData(filtersData?.customFiltersData, customFields);
      serverFilters.push(...dtFilter);
   }

   if (filtersData?.rolesFilterData.length > 0) {
      const dtFilters = convertRolesFilterToIServerFilters(filtersData?.rolesFilterData);
      serverFilters.push(...dtFilters);
   }

   const viewConfig = {
      ...defaultListTableConfig,
      columnState: finalColumnStateWithGroupSort,
      serverFilters: serverFilters,
      search: savedView.search,
      columnGroupState: columnGroupState,
   };

   return viewConfig;
}

function getSortDirection(sortDirection: Order) {
   return sortDirection == Order.ASCENDING ? "asc" : "desc";
}

function convertRolesFilterToIServerFilters(chipFilters: ChipFilter[]): IServerFilter[] {
   // This is really important for backwards compatibility. Some older saved views may have a value of ANY,
   // but we will convert that to undefined here to work with our new endpoint an our newly updated ProjectRolesFilter.
   chipFilters = chipFilters.map((filter) => {
      if (String(filter.value).includes("ANY")) {
         filter.value = undefined;
      }

      return filter;
   });
   try {
      return [
         {
            field: "project_roles",
            selected: chipFilters,
            value: chipFilters,
         } as any,
      ];
   } catch (error) {
      console.error("Error in convertRolesFilterToIServerFilters:", error);
      return [];
   }
}

function convertToIServerFilter(
   chipFilter: ChipFilter,
   filterFieldMap: ColumnMap,
   filterRendererMap: ColumnMap,
): IServerFilter | undefined {
   const { property, value_name, classifier, classifier_label } = chipFilter;
   const value = chipFilter.value as any;
   const field = filterFieldMap[property];
   const filterRenderer = filterRendererMap[property];
   switch (filterRenderer) {
      case MultiSelectPillFilter: //Previously, there was an issue with this filter type where it was returning undefined. To ensure proper values are displayed, we now need to handle two conditions.
         return {
            value: Array.isArray(value)
               ? value.map((x: any) => (typeof x === "object" ? x.value : x))
               : typeof value === "string"
               ? [value]
               : [],
            field: field,
            filterRenderer,
            selected: value,
         };
      case StatusFilter:
      case MultiSelectFilterRenderer:
         return {
            value: [{ value: value, label: value_name } as any],
            field: field,
            filterRenderer,
            selected: [
               {
                  value: value,
                  label: value_name,
               },
            ],
         };
      case NumericValueFilter:
         return {
            value: [
               {
                  value: value,
                  stringValue: value_name,
                  classifier: {
                     id: 1,
                     label: classifier_label,
                     value: classifier,
                  },
               } as any,
            ],
            field: field,
            filterRenderer,
            selected: [
               {
                  value: value,
                  stringValue: value_name,
                  classifier: {
                     id: 1,
                     label: classifier_label,
                     value: classifier,
                  },
               } as any,
            ],
         };
      case DateFilter:
         // case "customDateFilter":
         return convertDateChipFilter(field, filterRenderer, classifier, value, classifier_label);

      case HidePastTimeOffFilter:
         return {
            value: [{ value: value } as any],
            field: field,
            filterRenderer,
            selected: [
               {
                  value: value,
               },
            ],
         };
      default:
         return {
            value: [value],
            field: field,
            filterRenderer,
            selected: [{ value: value, label: value_name }],
         };
   }
}

function transformData(data: any[], customFields?: CustomField[]): any[] {
   // Define transformation mappings based on type
   const transformationMap: Record<string, (items: any[], field: string) => any> = {
      [CustomFieldType.BOOL]: (items, field) => {
         const transformedBoolData = items.map((item) => ({
            field: field,
            selected: [{ value: item.value, label: item.value ? "Yes" : "No" }],
            value: [{ value: item.value, label: item.value ? "Yes" : "No" }],
         }));

         // Merge all transformed bool data into a single object
         return {
            field: field, // Example field name for consolidated bool data
            filterRenderer: "customBoolFilterRenderer",
            selected: transformedBoolData.map((item) => item.selected[0]),
            value: transformedBoolData.map((item) => item.value[0]),
         };
      },
      [CustomFieldType.DATE]: (items, field) =>
         items.map((item) => ({
            field: field,
            filterRenderer: "customDateFilterRenderer",
            selected: [
               {
                  classifier: {
                     label: item.classifier_label,
                     value: item.classifier,
                  },
                  date: getAttachedDate(item.value).toISOString(),
               },
            ],
            value: [
               {
                  classifier: {
                     label: item.classifier_label,
                     value: item.classifier,
                  },
                  date: getAttachedDate(item.value).toISOString(),
               },
            ],
         })),
      [CustomFieldType.TEXT]: (items, field) =>
         items.map((item) => ({
            field: field,
            filterRenderer: "customTextFilterRenderer",
            selected: [{ value: item.value, label: item.value }],
            value: [{ value: item.value, label: item.value }],
         })),
      [CustomFieldType.CURRENCY]: (items, field) =>
         items.map((item) => ({
            field: field,
            filterRenderer: "customNumericValueFilter",
            selected: [
               {
                  classifier: {
                     label: item.classifier_label,
                     value: item.classifier,
                  },
                  value: item.value,
                  stringValue: item.value.toString(),
               },
            ],
            value: [
               {
                  classifier: {
                     label: item.classifier_label,
                     value: item.classifier,
                  },
                  value: item.value,
                  stringValue: item.value.toString(),
               },
            ],
         })),
      [CustomFieldType.NUMBER]: (items, field) =>
         items.map((item) => ({
            field: field,
            filterRenderer: "customNumericValueFilter",
            selected: [
               {
                  classifier: {
                     label: item.classifier_label,
                     value: item.classifier,
                  },
                  value: item.value,
                  stringValue: item.value.toString(),
               },
            ],
            value: [
               {
                  classifier: {
                     label: item.classifier_label,
                     value: item.classifier,
                  },
                  value: item.value,
                  stringValue: item.value.toString(),
               },
            ],
         })),
      [CustomFieldType.MULTI_SELECT]: (items, field) =>
         items.map((item) => ({
            field: field,
            filterRenderer: "multiSelectFilterRenderer",
            selected: item.value.map((x: any) => x.name),
            value: item.value.map((x: any) => x.name),
         })),
      [CustomFieldType.SELECT]: (items, field) =>
         items.map((item) => ({
            field: field,
            filterRenderer: "multiSelectFilterRenderer",
            selected:
               typeof item.value === "string" ? [item.value] : item.value.map((x: any) => x.name),
            value:
               typeof item.value === "string" ? [item.value] : item.value.map((x: any) => x.name),
         })),
   };

   // Transform data based on type using reduce
   return data.reduce((acc: any[], item: any) => {
      let field;

      // Check if this is an entity-specific field name (contains "EntityType's fieldname" pattern)
      const { type, field: parsedField } = parseAssignmentCustomField(item.filter_name);

      if (type && parsedField) {
         const customField = findCustomFieldByIntegrationName(customFields!, parsedField);
         field = customField ? `${type}'s ${customField.integration_name}` : item.filter_name;
      } else {
         field =
            customFields?.find((x) => x.id === item.custom_field_id)?.integration_name ??
            item.filter_name;
      }
      const transformFn = transformationMap[item.type];
      if (transformFn) {
         if (
            item.type === CustomFieldType.BOOL &&
            acc.length > 0 &&
            acc[acc.length - 1].filterRenderer === "customBoolFilterRenderer"
         ) {
            acc[acc.length - 1] = {
               ...acc[acc.length - 1],
               selected: acc[acc.length - 1].selected.concat(transformFn([item], field).selected),
               value: acc[acc.length - 1].value.concat(transformFn([item], field).value),
            };
         } else {
            const transformedData = transformFn([item], field);
            if (Array.isArray(transformedData)) {
               const maybeExistingFilter = acc.find((x) => x.field === transformedData[0].field);
               if (maybeExistingFilter) {
                  maybeExistingFilter.selected = maybeExistingFilter.selected.concat(
                     transformedData[0].selected,
                  );
                  maybeExistingFilter.value = maybeExistingFilter.value.concat(
                     transformedData[0].value,
                  );
               } else {
                  acc.push(...transformedData);
               }
            } else {
               acc.push(transformedData);
            }
         }
      }
      return acc;
   }, []);
}

function convertDateChipFilter(
   field: string,
   filterRenderer: any,
   classifier: string | null,
   value: any,
   classifier_label: string | null,
): IServerFilter | undefined {
   // We don't support With x days/weeks/months in the new page yet.
   // When we encounter one of those filters in a saved view, simply return undefined.
   if (classifier == "<=x<") {
      return;
   }
   const dateString = getAttachedDate(value).toISOString();
   return {
      value: [
         { date: dateString, classifier: { value: classifier, label: classifier_label } } as any,
      ],
      field: field,
      filterRenderer,
      selected: [
         { date: dateString, classifier: { value: classifier, label: classifier_label } } as any,
      ],
   };
}

export function parseAssignmentCustomField(field: string): { type?: string; field?: string } {
   const enumValues = Object.values(EntityTypes).join("|");
   const regex = new RegExp(`\\b(${enumValues})'\\s?s\\s+(.+)\\b`, "i");
   const match = regex.exec(field);
   if (match) {
      return { type: match[1], field: match[2] };
   }
   return {};
}

export function convertDataTableConfigToSavedView(
   viewConfig: DataTableConfig,
   columnHeadersMap: ColumnMap,
   filterNameMaps: ColumnMap,
   filterFieldMap: ColumnMap,
   filterRendererMap: ColumnMap,
   customFields?: CustomField[],
   page?: string,
): Partial<SavedView> {
   let sort_by;
   let sort_direction;
   let custom_field_id: string | undefined;
   const columnHeadersMapWithCustomFields = {
      ...columnHeadersMap,
      ...subPropertiesColumnHeaderMap,
      ...(customFields?.reduce((acc: any, field: any) => {
         acc[field.name] = field.integration_name;
         return acc;
      }, {}) ?? {}),
   };

   const subPropertiesColumns = viewConfig.columnState.filter((item) =>
      Object.prototype.hasOwnProperty.call(subPropertiesColumnHeaderMap, item.field),
   );
   // Remove sub-properties columns from the column state
   viewConfig.columnState =
      subPropertiesColumns.length > 0
         ? viewConfig.columnState.filter(
              (item) =>
                 !Object.prototype.hasOwnProperty.call(subPropertiesColumnHeaderMap, item.field),
           )
         : viewConfig.columnState;

   const groupSort = extractGroupColumnSort(
      viewConfig.columnState,
      columnHeadersMapWithCustomFields,
   );
   if (groupSort.sort_by) sort_by = groupSort.sort_by;
   if (groupSort.sort_direction) sort_direction = groupSort.sort_direction;

   const column_headers = viewConfig.columnState
      .filter(
         (x: any) =>
            x.field !== rowGroupColFieldName &&
            ((!x.hidden && x.rowGroup === false) || (x.hidden && x.rowGroup === true)),
      )
      .map((col: any, i: number) => {
         if (col.sort !== null) {
            sort_by = Object.keys(columnHeadersMapWithCustomFields).find(
               (key) => columnHeadersMapWithCustomFields[key] == col.field,
            );
            sort_direction = col.sort == "asc" ? Order.ASCENDING : Order.DESCENDING;
         }
         let key;
         let key_id;
         let meta;
         if (checkFilterIsRoles(col)) {
            key = col.field.split("_")[0];
            key_id = col.field.split("_")[1];
         } else {
            key =
               Object.keys(columnHeadersMapWithCustomFields).find(
                  (key) => columnHeadersMapWithCustomFields[key] == col.field,
               ) ?? "";
         }

         if (col.field === columnHeadersMap.current_assignments_project_name) {
            key = columnHeadersMap.current_assignments;
         }
         if (col.field === columnHeadersMap.next_assignment_project_name) {
            key = columnHeadersMap.next_assignment;
         }
         // for assignment list page we have to handle custom fields differently
         if (page === SavedViewPage.ASSIGNMENTS_LIST) {
            const { type, field } = parseAssignmentCustomField(col.field);
            let customField: CustomField | undefined;
            if (type && field) {
               customField = findCustomFieldByIntegrationName(customFields!, field);
               if (customField) {
                  key = columnHeadersMapWithCustomFields["custom_fields"];
                  meta = {
                     field_id: customField.id,
                     field_entity: entityTypeMap[type] as any,
                     field_type: customField.type as CustomFieldType,
                     field_property: customField.integration_name,
                  };
               }

               if (col.sort !== null) {
                  sort_by = customFieldSortEntityTypeMap[type];
                  sort_direction = col.sort == "asc" ? Order.ASCENDING : Order.DESCENDING;
                  custom_field_id = customField?.id;
               }
            }
         }

         // for request list page we have to handle custom fields differently
         if (page === SavedViewPage.REQUESTS) {
            const customField = findCustomFieldByIntegrationName(customFields!, col.field);
            if (customField) {
               key = columnHeadersMapWithCustomFields["custom_fields"];
               meta = {
                  field_id: customField.id,
                  field_entity: entityTypeMap[EntityTypes.REQUEST],
                  field_type: customField.type as CustomFieldType,
                  field_property: customField.integration_name,
               };
            }
         }

         return {
            key: key,
            key_id: key_id,
            width: Math.round(col.width),
            name: "",
            ...(key === "custom_fields" && { meta }), // Add meta field only if key=== "custom_fields"
            sequence: i,
            sortable: true,
            sub_properties: key ? [{ key: key, enabled: col.rowGroup, name: key }] : [], // This is a hack to make the column groupable
            pinned: col.pinned,
         };
      });

   if (subPropertiesColumns.length > 0) {
      // Create an object to store sub-properties dynamically
      const subProperties: SubProperties = {
         current_assignment_sub_properties: [],
         next_assignment_sub_properties: [],
      };

      subPropertiesColumns.forEach((col) => {
         if (col.sort !== null) {
            sort_by = Object.keys(subPropertiesColumnHeaderMap).find(
               (key) => subPropertiesColumnHeaderMap[key] == col.field,
            );
            sort_direction = col.sort == "asc" ? Order.ASCENDING : Order.DESCENDING;
         }
         // Check if column matches any field in the sub-properties config
         Object.keys(subPropertiesConfig).forEach((groupKey) => {
            const key = groupKey as keyof typeof subPropertiesConfig;
            subPropertiesConfig[key].forEach((subProp) => {
               if (col.field === subProp.field) {
                  // Create sub-property object
                  const subProperty = {
                     name: subProp.name,
                     key: subProp.key,
                     enabled: true,
                     width: Math.round(col.width!),
                  };

                  // Push to the corresponding group of sub-properties
                  subProperties[`${key}_sub_properties` as keyof SubProperties].push(subProperty);
               }
            });
         });
      });

      // Update columns of current and next assignments if sub-properties exist

      if (subProperties.current_assignment_sub_properties.length > 0) {
         const currentAssignmentColumn = column_headers?.find(
            (col) => col.key === columnHeadersMap.current_assignments,
         );

         if (currentAssignmentColumn) {
            currentAssignmentColumn.sub_properties =
               subProperties.current_assignment_sub_properties;
         }
      }

      if (subProperties.next_assignment_sub_properties.length > 0) {
         const nextAssignmentColumn = column_headers?.find(
            (col) => col.key === columnHeadersMap.next_assignment,
         );

         if (nextAssignmentColumn) {
            nextAssignmentColumn.sub_properties = subProperties.next_assignment_sub_properties;
         }
      }
   }

   let chip_filters: ChipFilter[] = [];

   viewConfig.serverFilters?.forEach((filter: IServerFilter) => {
      const name = filterNameMaps[filter.field];
      const property = Object.keys(filterFieldMap).find(
         (key: string) => filterFieldMap[key] == filter.field,
      )!;

      // If the filter is a custom field, find the custom field object
      if (!name && !property) {
         let field = filter.field;
         let customFieldFilterProperty = "custom_fields";
         // keeping this undefined here because we will only use it for assignments list page
         let originalFieldName;
         if (page === SavedViewPage.ASSIGNMENTS_LIST) {
            originalFieldName = filter.field;
            const { type, field: parsedField } = parseAssignmentCustomField(field);
            if (type && parsedField) {
               field = parsedField;
               customFieldFilterProperty = customFieldFilterEntityTypeMap[type] as string;
            }
         }
         const associatedCustomField = customFields
            ? findCustomFieldByIntegrationName(customFields, field)
            : undefined;

         if (associatedCustomField) {
            const cFilters = convertCustomFieldToChipFilter(
               filter,
               associatedCustomField,
               customFieldFilterProperty,
               originalFieldName,
            ) as ChipFilter[];
            chip_filters = chip_filters.concat(cFilters);
         }
      } else {
         const cFilters = convertToChipFilter(filter, filterRendererMap, name, property);
         chip_filters = chip_filters.concat(cFilters);
      }
   });
   return {
      chip_filters: chip_filters,
      view_config: {
         column_headers: column_headers,
         sort_by: sort_by,
         sort_direction: sort_direction,
         ...(custom_field_id && { custom_field_id }), // Add custom_field_id only if it exists
      },
   };
}
function convertToChipFilter(
   filter: IServerFilter,
   filterRendererMap: ColumnMap,
   name: string,
   property: string,
): ChipFilter[] {
   const filterRenderer = filterRendererMap[filter.field] ?? filterRendererMap[property]; // Fallback to property if field is not found

   switch (filterRenderer) {
      case MultiSelectPillFilter:
         return filter.selected.map((val: any) => {
            return {
               filter_name: name,
               negation: false,
               property: property,
               value: [val],
               value_name: val.label,
               classifier: null,
               classifier_label: null,
               custom_field_id: null,
            };
         });
      case StatusFilter:
         return filter.selected.map((val: any) => {
            return {
               filter_name: name,
               negation: false,
               property: property,
               value: val.value,
               value_name: val.label,
               classifier: null,
               classifier_label: null,
               custom_field_id: null,
            };
         });

      case DateFilter: {
         const value: any = filter.value[0];
         const date = new Date(value.date);
         const detachedDay = getDetachedDay(date);
         return [
            {
               filter_name: name,
               negation: false,
               property: property,
               value: detachedDay,
               value_name: `${value.classifier.label} ${date.toDateString()}`,
               classifier: value.classifier.value,
               classifier_label: value.classifier.label,
               custom_field_id: null,
            },
         ];
      }
      case NumericValueFilter:
         return filter.selected.map((val: any) => {
            const cFValue = val.value ? val.value : val.id;
            return {
               filter_name: name,
               negation: false,
               property: property,
               value: cFValue,
               classifier: val.classifier.value,
               value_name: cFValue.toString(),
               classifier_label: val.classifier.label,
               custom_field_id: val.custom_field_id,
            };
         });

      case HidePastTimeOffFilter:
         return [
            {
               filter_name: name,
               negation: false,
               property: property,
               value: true,
               value_name: "Hide",
               classifier: null,
               classifier_label: null,
               custom_field_id: null,
            },
         ];

      case ProjectRolesFilter:
         if (filter.selected.length === 0) return [];

         // Check for "people" filed for legacy filter support / backwards compatibility
         if ("people" in filter.selected[0]) {
            return filter.selected.flatMap((val) => {
               const roleId = val.role?.id;
               const roleLabel = val.role?.label;
               if (!val.people?.length || !roleId) return [];

               return val.people.map((person: PersonOption) => ({
                  filter_name: name,
                  negation: false,
                  property: property,
                  value: person.id,
                  value_name: person.label,
                  classifier: roleId,
                  classifier_label: roleLabel,
                  custom_field_id: null,
               }));
            });
         } else if ("job_title_name" in filter.selected[0]) {
            return filter.selected.map((role: RoleWithNamesAndColor) => ({
               filter_name: name,
               negation: false,
               property: property,
               value: role.resource_id,
               value_name: role.resource_first_name + " " + role.resource_last_name,
               classifier: role.job_title_id,
               classifier_label: role.job_title_name,
               custom_field_id: null,
               job_title_color: role.job_title_color,
            }));
         } else {
            console.error("Unfamiliar filter format:", filter);
            return [];
         }

      default:
         return filter.selected.map((val: any) => {
            const cFValue = val.value ? val.value : val.id;
            return {
               filter_name: name,
               negation: false,
               property: property,
               value: cFValue,
               classifier: null,
               value_name: val.label,
               classifier_label: null,
               custom_field_id: null,
            };
         });
   }
}
function convertCustomFieldToChipFilter(
   filter: IServerFilter,
   customField: CustomField,
   property: string,
   originalFieldName?: string,
) {
   const name = customField.name;
   const filterRenderer = CustomFieldFilterRendererMap[customField.type!];
   const cFValue = {
      filter_name: originalFieldName ?? name,
      negation: false,
      property,
      custom_field_id: customField.id,
      type: customField.type,
      timestamp: false,
   };
   const customFilters = filter.selected.map((val: any) => {
      switch (filterRenderer) {
         case BooleanFilter:
            return {
               ...cFValue,
               value: val.value,
               value_name: val.value.toString().toUpperCase(),
            };
         case NumericValueFilter:
            return {
               ...cFValue,
               value: val.value,
               value_name: val.value.toString(),
               classifier: val.classifier.value,
               classifier_label: val.classifier.label,
            };
         case DateFilter: {
            const date = new Date(val.date);
            const detachedDay = getDetachedDay(date);
            return {
               ...cFValue,
               value: detachedDay,
               value_name: `${val.classifier.label} ${date.toDateString()}`,
               classifier: val.classifier.value,
               classifier_label: val.classifier.label,
            };
         }
         case TextFilter:
            return { ...cFValue, value: val.value, value_name: val.value };
         default:
            break;
      }
   });
   if (filterRenderer === MultiSelectFilterRenderer) {
      const customFilters = {
         ...cFValue,
         value: filter.selected.map((x: any) => {
            return { value: x, name: x, selected: false };
         }),
         value_name: filter.selected.join(" & "),
      };

      return customFilters;
   }

   return customFilters;
}

export const getDownloadUrl = ({
   companyQrId,
   entityQrId,
   entityTitle,
   entitySubtitle,
   selectedSize,
   includeInfo,
   entityAbbreviation,
}: QRCodeDownloadURLProps) => {
   let baseUrl = `${requestContext.baseUrl}/download/qrcode?`;
   baseUrl += `cqi=${companyQrId}&eqi=${entityQrId}&epa=${entityAbbreviation}&`;

   if (entityTitle) {
      baseUrl += `title=${entityTitle}&`;
   }
   if (entitySubtitle) {
      baseUrl += `subtitle=${entitySubtitle}&`;
   }

   baseUrl += `width=${selectedSize}&info=${includeInfo}`;

   return baseUrl;
};

/*Function to check if the field is an integrated field and is locked in case of custom fields property matches with id
   and for common fields it matches with name*/
export function isIntegratedField(field: string, integratedFields: IntegratedField[]) {
   return integratedFields.some(
      (integratedField: IntegratedField) =>
         integratedField.property === field && integratedField.locked,
   );
}

export function isSensitiveField(
   field: string,
   sensitiveFields: any,
   canViewSensitiveFields: boolean,
   config: any,
   integration_name?: string,
) {
   // Create a map of column state by field name from the config coming from the local storage
   const columnStateMap = new Map(config.columnState.map((column: any) => [column.field, column]));

   const shouldHide = sensitiveFields.includes(field) || sensitiveFields.includes(integration_name);

   const shouldLockVisible = !shouldHide && canViewSensitiveFields;
   const propertiesDetails = { hidden: true, lockVisible: false };

   const existingConfig: any = columnStateMap.get(field);

   if (existingConfig) {
      if (shouldHide && !canViewSensitiveFields) {
         return { hidden: true, lockVisible: true };
      } else {
         return { hidden: existingConfig.hidden, lockVisible: false };
      }
   } else if (shouldLockVisible) {
      return { hidden: true, lockVisible: false };
   } else if (shouldHide && !canViewSensitiveFields) {
      return { hidden: true, lockVisible: true };
   }

   return propertiesDetails;
}

export function isSensitiveFieldEditable(
   field: string,
   projectsSensitiveFields: any,
   canEditProjectSensitiveFields: boolean,
) {
   const isFieldSensitive = projectsSensitiveFields.includes(field);
   // Logic to determine if the field should be disabled
   if (
      (isFieldSensitive && canEditProjectSensitiveFields) ||
      (!isFieldSensitive && canEditProjectSensitiveFields) ||
      (!isFieldSensitive && !canEditProjectSensitiveFields)
   ) {
      return false; // field is editable
   } else {
      return true; // field is not editable
   }
}

export function isFieldEditable(
   field: string,
   canEditDetails: boolean,
   integratedFields: IntegratedField[],
   sensitiveFields: any,
   canEditSensitiveFields: boolean,
): boolean {
   return (
      canEditDetails &&
      !isIntegratedField(field, integratedFields) &&
      !isSensitiveFieldEditable(field, sensitiveFields, canEditSensitiveFields) // Sensitive field logic
   );
}

export const formatOptions = (
   options?: RawOption[] | SerializedCachedEquipmentCompanyType[] | null,
): FormattedOption[] => {
   if (!options?.length) {
      return [];
   }
   return options
      .map((option: any) => ({
         id: option.id || option.value, // Use option.id if available, otherwise option.value
         label: option.name,
      }))
      .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label)); // Sort by label
};

/*
This function looks ridiculous, but the Core React PillSelect component does not support
custom colors, so we need to map the colors to the Procore approved colors.
We will eventually update our Status settings to only uses a specific set of colors,
but this will allow us to use the existing colors for now.
*/
export const getProcoreApprovedPillColor = (color: string): PillColor => {
   // Procore approved colors: https://core.procore.com/11.0.0/web/components/pill/react/
   const colors: Record<PillColor, string> = {
      blue: "#0000FF",
      cyan: "#00FFFF",
      gray: "#808080",
      green: "#008000",
      magenta: "#FF00FF",
      red: "#FF0000",
      yellow: "#FFFF00",
      UNSAFE_orange: "#FFA500",
   };

   const hexToRgb = (hex: string): [number, number, number] => {
      const hexWithoutHash = hex.startsWith("#") ? hex.slice(1) : hex;
      const bigint = parseInt(hexWithoutHash, 16);
      return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
   };

   const rgbToXyz = (r: number, g: number, b: number): [number, number, number] => {
      r = r / 255;
      g = g / 255;
      b = b / 255;
      r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
      g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
      b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
      return [
         (r * 0.4124 + g * 0.3576 + b * 0.1805) * 100,
         (r * 0.2126 + g * 0.7152 + b * 0.0722) * 100,
         (r * 0.0193 + g * 0.1192 + b * 0.9505) * 100,
      ];
   };

   const xyzToLab = (x: number, y: number, z: number): [number, number, number] => {
      x /= 95.047;
      y /= 100.0;
      z /= 108.883;
      x = x > 0.008856 ? Math.pow(x, 1 / 3) : 7.787 * x + 16 / 116;
      y = y > 0.008856 ? Math.pow(y, 1 / 3) : 7.787 * y + 16 / 116;
      z = z > 0.008856 ? Math.pow(z, 1 / 3) : 7.787 * z + 16 / 116;
      return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
   };

   const ciede2000 = (lab1: [number, number, number], lab2: [number, number, number]): number => {
      const [L1, a1, b1] = lab1;
      const [L2, a2, b2] = lab2;

      const avgL = (L1 + L2) / 2;
      const C1 = Math.sqrt(a1 * a1 + b1 * b1);
      const C2 = Math.sqrt(a2 * a2 + b2 * b2);
      const avgC = (C1 + C2) / 2;

      const G = 0.5 * (1 - Math.sqrt(Math.pow(avgC, 7) / (Math.pow(avgC, 7) + Math.pow(25, 7))));
      const a1Prime = a1 * (1 + G);
      const a2Prime = a2 * (1 + G);

      const C1Prime = Math.sqrt(a1Prime * a1Prime + b1 * b1);
      const C2Prime = Math.sqrt(a2Prime * a2Prime + b2 * b2);
      const avgCPrime = (C1Prime + C2Prime) / 2;

      const h1Prime = Math.atan2(b1, a1Prime) + (Math.atan2(b1, a1Prime) < 0 ? 2 * Math.PI : 0);
      const h2Prime = Math.atan2(b2, a2Prime) + (Math.atan2(b2, a2Prime) < 0 ? 2 * Math.PI : 0);

      const avgHPrime =
         Math.abs(h1Prime - h2Prime) > Math.PI
            ? (h1Prime + h2Prime + 2 * Math.PI) / 2
            : (h1Prime + h2Prime) / 2;

      const T =
         1 -
         0.17 * Math.cos(avgHPrime - Math.PI / 6) +
         0.24 * Math.cos(2 * avgHPrime) +
         0.32 * Math.cos(3 * avgHPrime + Math.PI / 30) -
         0.2 * Math.cos(4 * avgHPrime - (63 * Math.PI) / 180);

      const deltaHPrime = h2Prime - h1Prime;
      const deltaH = 2 * Math.sqrt(C1Prime * C2Prime) * Math.sin(deltaHPrime / 2);

      const deltaL = L2 - L1;
      const deltaC = C2Prime - C1Prime;

      const SL = 1 + (0.015 * Math.pow(avgL - 50, 2)) / Math.sqrt(20 + Math.pow(avgL - 50, 2));
      const SC = 1 + 0.045 * avgCPrime;
      const SH = 1 + 0.015 * avgCPrime * T;

      const deltaTheta = 30 * Math.exp(-Math.pow(((avgHPrime * 180) / Math.PI - 275) / 25, 2));
      const RC = 2 * Math.sqrt(Math.pow(avgCPrime, 7) / (Math.pow(avgCPrime, 7) + Math.pow(25, 7)));
      const RT = -RC * Math.sin((2 * deltaTheta * Math.PI) / 180);

      return Math.sqrt(
         Math.pow(deltaL / SL, 2) +
            Math.pow(deltaC / SC, 2) +
            Math.pow(deltaH / SH, 2) +
            RT * (deltaC / SC) * (deltaH / SH),
      );
   };

   const rgbColor = hexToRgb(color);
   const labColor = xyzToLab(...rgbToXyz(...rgbColor));
   let closestName: PillColor = "blue"; // Default to one of the options
   let minDistance = Infinity;

   for (const [name, hexValue] of Object.entries(colors)) {
      const labValue = xyzToLab(...rgbToXyz(...hexToRgb(hexValue)));
      const distance = ciede2000(labColor, labValue);
      if (distance < minDistance) {
         minDistance = distance;
         closestName = name as PillColor;
      }
   }

   return closestName;
};

export const formatPillOptions = (options: RawOption[]): PillOption[] => {
   if (!options.length) {
      return [];
   }

   // If the options include a sequence field, sort by sequence, if not then sort by name
   return options
      .sort(
         (a: any, b: any) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.name.localeCompare(b.name),
      )
      .map((option: any) => ({
         id: option.id || option.value,
         label: option.name,
         color: getProcoreApprovedPillColor(option.color),
      }));
};

export const generateQuantityOptions = (min: number, max: number) => {
   return Array.from({ length: max - min + 1 }, (_, index) => {
      const value = (min + index).toString(); // Generate value from min to max
      return { id: value, label: value };
   });
};

export function getFilteredFields(
   fields: ProjectDetailsFields[] | PersonDetailFields[],
   projectSensitiveFields: string[],
   canViewProjectSensitiveFields: boolean,
   canViewProjectFinancials: boolean,
) {
   return fields.filter((field: ProjectDetailsFields | PersonDetailFields) => {
      if (field.type === CustomFieldType.CURRENCY) {
         return canViewProjectFinancials;
      }
      return !projectSensitiveFields.includes(field.name) || canViewProjectSensitiveFields;
   });
}

export function commonButtonStyle({
   selectedId,
   currentId,
}: CommonStyleProps): React.CSSProperties {
   return {
      cursor: "pointer",
      pointerEvents: selectedId && selectedId !== currentId ? "none" : "auto",
      color: selectedId && selectedId !== currentId ? "hsl(200, 8%, 70%)" : "black",
   };
}

export function getReorderedColumnDefinition(
   columnDefinitions: ColumnDefinition[],
   tableConfigColumnState: ColumnDefinition[],
   calledFrom?: PageType,
) {
   const orderMappingOfTableConfig = new Map(
      tableConfigColumnState.map((item: ColumnDefinition, index: any) => [item.field, index]),
   );

   const reorderedColumnDefinitions = columnDefinitions.slice().sort((a, b) => {
      const orderA = orderMappingOfTableConfig.get(a.field) as number;
      const orderB = orderMappingOfTableConfig.get(b.field) as number;
      return (
         (orderA !== undefined ? orderA : Infinity) - (orderB !== undefined ? orderB : Infinity)
      );
   });

   if (calledFrom === PageType.PERSON) {
      const finalSortedColumns = sortGroups(reorderedColumnDefinitions, orderMappingOfTableConfig);
      return finalSortedColumns as ColumnDefinition[];
   }
   return reorderedColumnDefinitions as ColumnDefinition[];
}

// this function is used to sort the columns based on the order in the table config for nested columns
export const sortGroups = (columns: any[], orderMappingOfTableConfig: any) => {
   // Step 1: Sort children within groups
   const updatedColumns = columns.map((column) => {
      if (column.children) {
         const sortedChildren = column.children.slice().sort((a: any, b: any) => {
            const orderA = orderMappingOfTableConfig.get(a.field) ?? Infinity;
            const orderB = orderMappingOfTableConfig.get(b.field) ?? Infinity;
            return orderA - orderB;
         });
         return {
            ...column,
            children: sortedChildren,
         };
      }
      return column;
   });

   // Step 2: Reposition grouped columns based on their first child's position
   updatedColumns.sort((a: any, b: any) => {
      const firstChildA = a.children?.[0]?.field;
      const firstChildB = b.children?.[0]?.field;

      const orderA = firstChildA
         ? orderMappingOfTableConfig.get(firstChildA) ?? Infinity
         : orderMappingOfTableConfig.get(a.field) ?? Infinity;
      const orderB = firstChildB
         ? orderMappingOfTableConfig.get(firstChildB) ?? Infinity
         : orderMappingOfTableConfig.get(b.field) ?? Infinity;

      return orderA - orderB;
   });

   return updatedColumns;
};

const CustomFieldFilterRendererMap: ColumnMap = {
   select: MultiSelectFilterRenderer,
   "multi-select": MultiSelectFilterRenderer,
   bool: BooleanFilter,
   number: NumericValueFilter,
   text: TextFilter,
   date: DateFilter,
   currency: NumericValueFilter,
};

export const getUserName = (personName: PersonName, isLastNameFirst: boolean): string => {
   const { first = "", last = "" } = personName ?? {};
   return isLastNameFirst ? `${last} ${first}`.trim() : `${first} ${last}`.trim();
};

export const getDataForGrouping = async (
   data: any[],
   rowGroupCols: ColumnDefinition[],
   groupKeys: string[] | undefined,
   page: string,
) => {
   if (rowGroupCols?.length > 0) {
      const allGroups: { [key: string]: Set<any> } | undefined = rowGroupCols?.reduce(
         (acc, groupField) => {
            // get unique group names, keyed by column field of the grouped column
            // Filter out rows where row[groupField.field] is undefined
            const uniqueValues = new Set(
               data
                  .map((row: any) => {
                     if (
                        groupField.field === GroupByColumn.JobTitle &&
                        page === ReportType.PeopleList
                     ) {
                        return row["position"]?.name ?? "";
                     }
                     if (
                        groupField.field === GroupByColumn.Project &&
                        page === ReportType.RequestList
                     ) {
                        return row["project"] ?? "";
                     }
                     return row[groupField.field];
                  })
                  .filter((value: any) => value !== undefined), // Skip undefined values
            );

            const sortedUniqueValues = new Set(
               Array.from(uniqueValues).sort((a, b) => {
                  // If both a and b are empty or undefined, keep their order
                  if (!a && b) return 1; // Blank values should come last
                  if (a && !b) return -1; // Non-blank values should come first
                  return 0; // Otherwise, keep their relative order
               }),
            );
            return {
               ...acc,
               [groupField.field]: sortedUniqueValues,
            };
         },
         {},
      );
      // Filter data based on group keys, Currently, the Group By option is available for a single column, allowing only one group to be expanded at a time.
      // We can expand this to support multiple groups in the future.
      if (groupKeys?.length === rowGroupCols?.length) {
         data = data.filter((row: any) => {
            return groupKeys?.every((key, i) => {
               if (
                  rowGroupCols?.[i]?.field === GroupByColumn.JobTitle &&
                  page === ReportType.PeopleList
               ) {
                  return (row["position"]?.name ?? "") === key;
               }
               if (
                  rowGroupCols?.[i]?.field === GroupByColumn.Project &&
                  page === ReportType.RequestList
               ) {
                  return (row["project"] ?? "") === key;
               }
               return row[rowGroupCols?.[i]?.field ?? ""] === key;
            });
         });
      } else {
         // Return just the initial groups and the aggregation of their measures
         data = [...(allGroups?.[rowGroupCols?.[0]?.field ?? ""] ?? [])]?.map((groupName) => ({
            [rowGroupCols?.[0]?.field ?? ""]: groupName,
            id: groupName,
            excluded: true,
         }));
      }
   }
   return data;
};
export const getInitialsFromName = (personName: PersonName): string => {
   const { first = "", last = "" } = personName ?? {};
   return (first ? first[0] : "") + (last ? last[0] : "");
};

export const formatDateTime = (
   value: Date | number,
   options: {
      locale: string; // this we will be passing from I18nProvider
      isRequiredTimezone?: boolean; // if true, will include timeZone in the options
      dateStyle?: string; // default value set can be overridden
      timeZone?: string; // default value set can be overridden
   },
): string => {
   const { locale, isRequiredTimezone = true, dateStyle = "short", timeZone = "UTC" } = options;

   const dateTimeOptions = {
      locale,
      dateStyle,
      ...(isRequiredTimezone ? { timeZone } : {}), // Add timeZone if isRequiredTimezone is true
   };
   const dateFormatter = new DateTimeFormatter(dateTimeOptions as DateTimeOptions);
   return dateFormatter.formatDateTime(value);
};

export const mapServerFilters = (serverFilters: ListServerFilter[], customFields: CustomField[]) =>
   serverFilters.map((filter) => {
      const customField = findCustomFieldByIntegrationName(customFields, filter.field);
      return customField
         ? {
              ...filter,
              fieldId: customField.id,
              fieldType: customField.type,
           }
         : filter;
   });

/**
 * Converts a detached day numeric value into a locale-aware Date.
 * If the input is null, returns null.
 *
 * @param date - Detached day/timestamp value or null
 * @param I18n - I18n context providing locale information
 * @returns A JavaScript Date representing the localized value, or null
 */
export const getDate = (
   date: number | null,
   I18n: ReturnType<typeof useI18nContext>,
): Date | null =>
   date
      ? new Date(
           formatDateTime(getAttachedDate(date), {
              locale: I18n.locale,
              isRequiredTimezone: false,
           }),
        )
      : null;

/**
 * Returns the time option for a given time identifier.
 * If the input is null or not found, returns null.
 *
 * @param time - Time identifier or null
 * @returns The localized time option
 */
export const getTimeOption = (time: number | null): { id: number; label: string } | null =>
   time != null && time != undefined ? timeOptions.find(({ id }) => id === time) ?? null : null;

/**
 * Maps a serialized status to a render-ready object.
 *
 * @param status - Serialized status with name and color
 * @returns An object with label, color and a fixed shape of 'circle'
 */
export const setStatusValue = (
   status: SerializedStatus,
): { label: string; color: string; shape: "circle" } => {
   return {
      label: status?.name ?? "",
      color: status?.color ?? "",
      shape: "circle",
   };
};

export type WorkDaysObject = {
   [key in "0" | "1" | "2" | "3" | "4" | "5" | "6"]: boolean;
};

/**
 * Returns true if the date is a valid work day, false otherwise.
 *
 * @param date - Date to check
 * @param workDaysObject - Work days object
 * @returns true if the date is a valid work day, false otherwise
 */
export const getSelectedDay = (date: Date, workDaysObject: WorkDaysObject): boolean | null => {
   if (!date || !workDaysObject) {
      return null;
   }

   // Validate the date
   const validDate =
      date instanceof Date && !isNaN(date.getTime())
         ? new Date(
              formatDateTime(date, {
                 locale: "en", // Using 'en' to ensure consistent parsing
                 isRequiredTimezone: true,
                 timeZone: "UTC",
              }),
           )
         : null;

   if (!validDate) {
      return null;
   }

   // Get the day of the week
   const day = validDate.getDay().toString() as keyof WorkDaysObject;

   const isWorkDay = workDaysObject[day];

   if (isWorkDay === undefined) {
      return null;
   }

   return isWorkDay;
};

/**
 * Unified value validator for request list date cells (start_day and end_day).
 * - Validates required value
 * - Validates chronological order (end before start)
 * - Validates active work day based on provided work_days
 *
 * @param params - Value validator params containing { value, data }
 * @param I18n - I18n context for translations
 * @param isStartDay - boolean to indicate if the date is a start day
 * @returns ValueValidatorResponse
 * - isValid: boolean to indicate if the value is valid
 * - errorMessage: string to indicate the error message
 * - isRequired: boolean to indicate if the value is required
 */
export const validateDateCellForInlineEdit = (
   params: ValueValidatorParams<string | Date | undefined>,
   I18n: ReturnType<typeof useI18nContext>,
   isStartDay: boolean,
): ValueValidatorResponse => {
   const { value, data } = params;

   // Required field check
   if (!value) {
      return {
         isValid: false,
         errorMessage: I18n.t("views.company.workforce_planning.validations.required_field"),
         isRequired: true,
      };
   }

   // Chronological check (end before start)
   if (data?.end_day && data.end_day < data.start_day) {
      const msgKey = isStartDay
         ? "views.company.workforce_planning.assignment.bulk.edit.validation.start_date"
         : "views.company.workforce_planning.validations.end_date_before_start_date";
      return {
         isValid: false,
         errorMessage: I18n.t(msgKey, {
            date: formatDateTime(data.end_day as Date, {
               locale: I18n.locale,
               isRequiredTimezone: false,
            }),
         }),
         isRequired: true,
      };
   }

   // Active day check
   const isValidDate = value instanceof Date && !isNaN(value.getTime());
   const activeDay = isValidDate ? getSelectedDay(value as Date, data?.work_days_object) : null;
   if (!activeDay) {
      const msgKey = isStartDay
         ? "views.company.workforce_planning.assignment.bulk.edit.validation.start_date_active_day"
         : "views.company.workforce_planning.assignment.bulk.edit.validation.end_date_active_day";
      return {
         isValid: false,
         errorMessage: I18n.t(msgKey),
         isRequired: true,
      };
   }

   return { isValid: true, isRequired: true, errorMessage: "" };
};

/**
 * Prepares a date cell for inline edit:
 * - Guards against empty values
 * - Guards invalid chronological order
 * - Guards inactive work day
 * - Returns detached UTC day value if valid, otherwise undefined
 *
 * @param params - Cell value change params containing { newValue, rowData }
 * @param isStartDay - boolean to indicate if the date is a start day
 * @returns The formatted date if valid, otherwise undefined
 */
export const prepareDateCellForInlineEdit = (
   params: CellValueChangeParams<string | Date | undefined>,
   isStartDay: boolean,
): Date | undefined => {
   if (!params?.newValue) return;

   const { newValue, rowData } = params;
   const startDate: Date | undefined =
      (isStartDay ? newValue : rowData?.start_day) || rowData?.start_day;
   const endDate: Date | undefined = (isStartDay ? rowData?.end_day : newValue) || rowData?.end_day;

   // Chronology guard
   if (startDate && endDate && endDate.getTime?.() < startDate.getTime?.()) return;

   // Active work-day guard
   const selected = getSelectedDay(
      (isStartDay ? startDate : endDate) as Date,
      rowData?.work_days_object,
   );
   if (!selected) return;

   // Normalize to UTC day then detach
   const formattedDate = new Date(
      formatDateTime(newValue as Date, {
         locale: "en",
         isRequiredTimezone: true,
         timeZone: "UTC",
      }),
   );

   return formattedDate;
};

/**
 * Builds a standardized tooltip config from a message string.
 * Use this with inline conditions at call sites to avoid branching here.
 */
export const createInlineTooltip = (overlay: string) => ({
   trigger: "hover",
   showDelay: 400,
   placement: "top",
   hideDelay: 1000,
   overlay,
});
